import React, { useState } from "react";
import { X, Mail, ChevronDown, ChevronUp } from "lucide-react";
import { api, storage } from "../lib/api";

/**
 * EmailNicknameModal — Kiltro Scout end-user registration.
 *
 * Fields:
 *  - email (required, valid format)
 *  - nickname (optional, ^[A-Za-z0-9]+$, else server auto-assigns Kiltro_XXXX)
 *  - Checkbox 1: promo opt-in (Kiltro Scout own promos)
 *  - Checkbox 2: data-share opt-in (activity data to sponsors)
 *      -> accordion "Ver qué implica esto"
 *  - Static privacy disclaimer (always visible, NOT a checkbox)
 *
 * Textos son EXACTOS a la especificación del cliente — no modificar.
 */

const CHECKBOX1_TEXT =
  "Quiero recibir cupones y promociones de Kiltro Scout para aprovechar todas las oportunidades del equipo explorador.";
const CHECKBOX2_TEXT =
  "Autorizo a Kiltro Scout a compartir mis datos de actividad (locales visitados, horarios y reseñas).";
const DATA_SHARE_DETAIL =
  "Estos datos se pueden compartir con marcas y auspiciadores de Kiltro Scout para ofrecer más y mejores promociones y/o convenios a ti y a nuestra comunidad.";
const DISCLAIMER_TEXT =
  "No usaremos tu correo para enviarte publicidad, salvo que tú lo autorices marcando las opciones de arriba.";

const NICKNAME_RE = /^[A-Za-z0-9]+$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EmailNicknameModal({ open, onClose, onDone, title = "Únete a la camada" }) {
  const [email, setEmail] = useState(storage.getUserEmail());
  const [nickname, setNickname] = useState(storage.getUserNickname());
  const [promoOptin, setPromoOptin] = useState(false);
  const [dataShareOptin, setDataShareOptin] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const cleanEmail = email.trim().toLowerCase();
    const cleanNick = nickname.trim();

    if (!cleanEmail || !EMAIL_RE.test(cleanEmail)) {
      setErr("Ingresa un correo válido.");
      return;
    }
    if (cleanNick && !NICKNAME_RE.test(cleanNick)) {
      setErr("Nickname: solo letras y números, sin espacios ni símbolos.");
      return;
    }
    if (cleanNick && (cleanNick.length < 2 || cleanNick.length > 24)) {
      setErr("Nickname: entre 2 y 24 caracteres.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.registerUser({
        email: cleanEmail,
        nickname: cleanNick || null,
        device_id: storage.getDeviceId(),
        promo_optin: promoOptin,
        data_share_optin: dataShareOptin,
      });
      storage.setUserSession({
        user_id: res.user_id,
        nickname: res.nickname,
        email: cleanEmail,
      });
      onDone?.({ user_id: res.user_id, nickname: res.nickname });
      onClose?.();
    } catch (ex) {
      const msg = ex?.response?.data?.detail || "No pudimos registrarte. Intenta de nuevo.";
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="dialog-legible fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="email-nickname-modal"
    >
      <div
        className="bg-[#1A1A1A] border-2 border-[#FEBA01] w-full max-w-md shadow-brutal my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b-2 border-[#333]">
          <div className="flex items-center gap-2 font-display text-xl uppercase">
            <Mail size={20} className="text-[#FEBA01]" /> {title}
          </div>
          <button
            onClick={onClose}
            className="text-[#A3A3A3] hover:text-[#FEBA01]"
            data-testid="email-modal-close"
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <p className="text-sm text-[#A3A3A3] leading-relaxed">
            Antes de dejar tu reseña, cuéntanos quién eres. Solo pediremos esto una vez desde este dispositivo.
          </p>

          {/* Email */}
          <div>
            <label className="block font-body text-xs uppercase tracking-wider font-bold text-[#A3A3A3] mb-1">
              Correo <span className="text-[#FEBA01]">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setErr(""); }}
              autoFocus
              required
              placeholder="tucorreo@ejemplo.cl"
              className="w-full bg-[#0A0A0A] border-2 border-[#333] focus:border-[#FEBA01] outline-none px-3 py-2 text-[#F5F5F5] font-body"
              data-testid="email-modal-email-input"
            />
          </div>

          {/* Nickname */}
          <div>
            <label className="block font-body text-xs uppercase tracking-wider font-bold text-[#A3A3A3] mb-1">
              Nickname <span className="text-[#666] normal-case">(opcional)</span>
            </label>
            <input
              type="text"
              value={nickname}
              onChange={(e) => { setNickname(e.target.value); setErr(""); }}
              maxLength={24}
              placeholder="ElKiltro (solo letras y números)"
              className="w-full bg-[#0A0A0A] border-2 border-[#333] focus:border-[#FEBA01] outline-none px-3 py-2 text-[#F5F5F5] font-body"
              data-testid="email-modal-nickname-input"
            />
            <p className="mt-1 text-[11px] text-[#666]">
              Si lo dejas vacío, te asignamos uno tipo <span className="text-[#FEBA01]">Kiltro_0421</span>.
            </p>
          </div>

          {/* Checkbox 1 — promo opt-in */}
          <label className="flex items-start gap-3 cursor-pointer group" data-testid="email-modal-checkbox-promo-label">
            <input
              type="checkbox"
              checked={promoOptin}
              onChange={(e) => setPromoOptin(e.target.checked)}
              className="mt-1 w-5 h-5 accent-[#FEBA01] shrink-0"
              data-testid="email-modal-checkbox-promo"
            />
            <span className="text-sm text-[#F5F5F5] leading-relaxed group-hover:text-white">
              {CHECKBOX1_TEXT}
            </span>
          </label>

          {/* Checkbox 2 — data share opt-in */}
          <div>
            <label className="flex items-start gap-3 cursor-pointer group" data-testid="email-modal-checkbox-datashare-label">
              <input
                type="checkbox"
                checked={dataShareOptin}
                onChange={(e) => setDataShareOptin(e.target.checked)}
                className="mt-1 w-5 h-5 accent-[#FEBA01] shrink-0"
                data-testid="email-modal-checkbox-datashare"
              />
              <span className="text-sm text-[#F5F5F5] leading-relaxed group-hover:text-white">
                {CHECKBOX2_TEXT}
              </span>
            </label>
            <button
              type="button"
              onClick={() => setShowDetail((s) => !s)}
              className="mt-1 ml-8 flex items-center gap-1 text-[12px] text-[#FEBA01] underline decoration-dotted hover:text-white"
              data-testid="email-modal-datashare-toggle"
              aria-expanded={showDetail}
            >
              Ver qué implica esto
              {showDetail ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
            {showDetail && (
              <div
                className="mt-2 ml-8 p-3 bg-[#0A0A0A] border border-[#333] text-[13px] text-[#A3A3A3] leading-relaxed"
                data-testid="email-modal-datashare-detail"
              >
                {DATA_SHARE_DETAIL}
              </div>
            )}
          </div>

          {/* Static privacy disclaimer */}
          <div
            className="p-3 border-2 border-dashed border-[#333] bg-[#0A0A0A] text-[12px] text-[#A3A3A3] leading-relaxed"
            data-testid="email-modal-disclaimer"
          >
            {DISCLAIMER_TEXT}
          </div>

          {err && (
            <div className="text-sm text-red-400 border border-red-500/40 bg-red-500/10 p-2" data-testid="email-modal-error">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="btn-brutal w-full bg-[#FEBA01] text-black font-display text-lg uppercase py-3 shadow-brutal-white disabled:opacity-50"
            data-testid="email-modal-submit"
          >
            {submitting ? "Guardando…" : "Entrar a la camada"}
          </button>
        </form>
      </div>
    </div>
  );
}
